import { createSelector } from '@reduxjs/toolkit';
import { getAllSpans, mergeOverlappingSpanAttributes } from '../utils/annotation/span-utils';

const selectInvoiceList = (state: Accelerate.GradeBenchPanelState | undefined) =>
  state?.invoices ?? [];

const selectLooseSpanList = (state: Accelerate.GradeBenchPanelState | undefined) =>
  state?.looseSpans ?? [];

/**
 * Selector to derive all span annotations from invoices.
 * Memoized so useSelector does not see a new array reference on every render when inputs are unchanged.
 */
export const selectSpanAnnotations = createSelector(
  [selectInvoiceList, selectLooseSpanList],
  (invoices, looseSpans): Accelerate.SpanAnnotation[] => {
    if (!invoices.length && !looseSpans.length) {
      return [];
    }
    const codeSpans = getAllSpans(invoices);
    const allSpans = [...codeSpans, ...looseSpans];
    return mergeOverlappingSpanAttributes(allSpans);
  }
);

/**
 * Selector to get spans for a specific document ID
 * Useful for filtering spans by document in case-note views
 */
export const selectSpansByDocumentId = (state: Accelerate.GradeBenchPanelState | undefined, documentId: number): Accelerate.SpanAnnotation[] => {
  const spanAnnotations = selectSpanAnnotations(state);
  return spanAnnotations.filter(span => span.documentId === documentId);
};

/**
 * Selector to get only loose spans (not attached to any code)
 * Useful for operations that need to distinguish between code-attached and loose spans
 */
export const selectLooseSpans = (state: Accelerate.GradeBenchPanelState | undefined): Accelerate.SpanAnnotation[] => {
  return state?.looseSpans || [];
};


export const spanSelectors = {
  selectSpanAnnotations,
  selectSpansByDocumentId,
  selectLooseSpans
};